import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import {
  configProps,
  Driver,
  DriverFormContentType,
  Dropdowns,
  InputContext,
} from "../../../types/shared.ts";
import { extractApiError } from "../../../api/errors.ts";
import { DropdownOpenAddNew } from "../../../components/DropdownOpenAddNew.tsx";
import { useAppDispatch, useAppSelector } from "../../../redux/store.ts";
import { createFormSlice } from "../../../redux/slices/createForm.ts";
import { addNewSlice } from "../../../redux/slices/addNew.ts";
import { useSetDriverData } from "../../../hooks/useSetDataByView/useDriverData.ts";

import { useShowToast } from "../../../contexts/ToastContext.tsx";

type DriverDialogProps = {
  columns: configProps<"drivers">["columns"];
  dropdownConfigs: Pick<
    Dropdowns,
    "trucks" | "trailers" | "transporters" | "organizationalUsers"
  >;
  inputContext?: InputContext;
};

export default function AddNewDriver({
  columns: _columns,
  dropdownConfigs,
  inputContext,
}: DriverDialogProps) {
  const columns = useMemo(() => {
    return _columns.filter((column) => column !== undefined);
  }, [_columns]);
  const [entryFormContent, setEntryFormContent] =
    useState<DriverFormContentType>({
      firstName: "",
      lastName: "",
      personalCode: "",
      phone: "",
      email: "",
      companyId: null,
      managerId: null,
      trucksIds: [],
      trailersIds: [],
      driverId: undefined,
    });
  const [isSubmitting, setSubmitting] = useState(false);

  const dispatch = useAppDispatch();
  const { isOpen, mode, initialValues } = useAppSelector(
    (state) => state.createForm,
  );
  const { lastAdded } = useAppSelector((state) => state.addNew);

  const dropdownFilterRef = useRef<string>("");

  const showToast = useShowToast();
  const { setData } = useSetDriverData();

  // set the value of the dropdpwn to the newly added value
  useEffect(() => {
    if (lastAdded && lastAdded.field && lastAdded.value) {
      setEntryFormContent((prev) => {
        const field = lastAdded.field;
        if (field !== "trucksIds" && field !== "trailersIds") return prev;
        const prevArr = Array.isArray(prev[field]) ? prev[field] : [];
        // Prevent duplicates

        if (prevArr.includes(lastAdded.value as number)) return prev;
        return {
          ...prev,
          [field]: [...prevArr, lastAdded.value],
        };
      });
      dispatch(addNewSlice.actions.clearLastAdded());
    }
  }, [lastAdded, dispatch]);

  useEffect(() => {
    setEntryFormContent({
      firstName:
        typeof initialValues?.firstName === "string"
          ? initialValues.firstName
          : "",
      lastName:
        typeof initialValues?.lastName === "string"
          ? initialValues.lastName
          : "",
      personalCode:
        typeof initialValues?.personalCode === "string"
          ? initialValues.personalCode
          : "",
      phone:
        typeof initialValues?.phone === "string" ? initialValues.phone : "",
      email:
        typeof initialValues?.email === "string" ? initialValues.email : "",
      companyId:
        typeof initialValues?.companyId === "number"
          ? initialValues.companyId
          : null,
      managerId:
        typeof initialValues?.managerId === "number"
          ? initialValues.managerId
          : null,
      trucksIds: Array.isArray(initialValues?.trucksIds)
        ? initialValues.trucksIds.filter(
            (t): t is number => typeof t === "number",
          )
        : [],
      trailersIds: Array.isArray(initialValues?.trailersIds)
        ? initialValues.trailersIds.filter(
            (t): t is number => typeof t === "number",
          )
        : [],
      driverId:
        typeof initialValues?.driverId === "number"
          ? initialValues.driverId
          : undefined,
    });
  }, [initialValues]);

  const isFormValid = useMemo(() => {
    return (
      entryFormContent.firstName?.trim() !== "" &&
      entryFormContent.lastName?.trim() !== ""
    );
  }, [entryFormContent.firstName, entryFormContent.lastName]);

  const handleCellContentChange = (field: string, value: unknown) => {
    setEntryFormContent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isFormValid) {
      showToast({
        severity: "error",
        summary: "Błąd",
        detail: "Wszystkie pola są wymagane.",
      });
      return;
    }
    setSubmitting(true);

    const newEntry = {
      id: entryFormContent.driverId ?? undefined,
      firstName: entryFormContent.firstName,
      lastName: entryFormContent.lastName,
      personalCode: entryFormContent.personalCode ?? undefined,
      phone: entryFormContent.phone ?? undefined,
      email: entryFormContent.email ?? undefined,
      companyId: entryFormContent.companyId ?? undefined,
      managerId: entryFormContent.managerId ?? undefined,
      vehicleIds: [
        ...(entryFormContent.trucksIds ?? []),
        ...(entryFormContent.trailersIds ?? []),
      ],
    };

    try {
      console.log("Saving driver:", entryFormContent, newEntry);
      const setDataResponse = await setData([newEntry]);
      if (
        setDataResponse === null ||
        setDataResponse?.some((entry) => !!entry?.error)
      ) {
        const errorObj =
          setDataResponse?.find((entry) => !!entry?.error)?.error ?? null;
        if (errorObj) {
          showToast({
            severity: "error",
            summary: "Błąd",
            detail: extractApiError(errorObj),
          });
        }
        return;
      }

      if (inputContext === "driverDialog") {
        showToast({
          severity: "success",
          summary: "Sukces",
          detail: "Dodano nowego kierowcę",
        });
      } else {
        showToast({
          severity: "success",
          summary: "Sukces",
          detail: "Zaktualizowano kierowcę",
        });
      }

      setEntryFormContent({
        firstName: "",
        lastName: "",
        personalCode: "",
        phone: "",
        email: "",
        companyId: null,
        managerId: null,
        trucksIds: [],
        trailersIds: [],
        driverId: undefined,
      });
      dispatch(createFormSlice.actions.closeForm());
    } catch (error) {
      console.error("Error saving driver:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const onClose = () => {
    dispatch(createFormSlice.actions.closeForm());
    setEntryFormContent({
      firstName: "",
      lastName: "",
      personalCode: "",
      phone: "",
      email: "",
      companyId: null,
      managerId: null,
      trucksIds: [],
      trailersIds: [],
      driverId: undefined,
    });
    dropdownFilterRef.current = "";
  };

  const trucksColumn = columns.find((column) => column.field === "trucks");
  const trailersColumn = columns.find((column) => column.field === "trailers");

  if (
    !trucksColumn ||
    !trailersColumn ||
    trucksColumn.allowAddNew == undefined ||
    trucksColumn.allowAddNew == "product" ||
    trailersColumn.allowAddNew == undefined ||
    trailersColumn.allowAddNew == "product"
  ) {
    console.error("Trucks or Trailers columns not configured properly.");
    return null;
  }

  return (
    <>
      <Dialog
        header={mode === "edit" ? "Edytuj kierowcę" : "Dodaj nowego kierowcę"}
        visible={isOpen && (mode === "create" || mode === "edit")}
        onHide={() => onClose()}
        breakpoints={{ "2000px": "40vw", "960px": "75vw", "641px": "95vw" }}
        footer={
          <>
            <Button
              label="Anuluj"
              onClick={() => onClose()}
              className="p-button-text"
            />
            <Button
              label="Zapisz"
              onClick={() => {
                void handleSave().catch(console.log);
              }}
              disabled={!isFormValid || isSubmitting}
            />
          </>
        }
      >
        <div className="field">
          <label>
            Imię<span style={{ color: "red" }}> *</span>
          </label>
          <InputText
            value={entryFormContent.firstName}
            onChange={(e) =>
              setEntryFormContent((prev) => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
            placeholder="Wprowadź imię"
            className="w-full"
          />
        </div>
        <div className="field">
          <label>
            Nazwisko<span style={{ color: "red" }}> *</span>
          </label>
          <InputText
            value={entryFormContent.lastName}
            onChange={(e) =>
              setEntryFormContent((prev) => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
            placeholder="Wprowadź nazwisko"
            className="w-full"
          />
        </div>
        <div className="field">
          <label>ID</label>
          <InputText
            value={entryFormContent.personalCode}
            maxLength={11}
            onChange={(e) =>
              setEntryFormContent((prev) => ({
                ...prev,
                personalCode: e.target.value,
              }))
            }
            placeholder="Napisz kod identyfikacyjny"
            className="w-full"
          />
        </div>
        <div className="field">
          <label>Telefon</label>
          <InputText
            value={entryFormContent.phone}
            max={128}
            onChange={(e) =>
              setEntryFormContent((prev) => ({
                ...prev,
                phone: e.target.value,
              }))
            }
            placeholder="Napisz numer telefonu"
            className="w-full"
          />
        </div>
        <div className="field">
          <label>E-mail</label>
          <InputText
            value={entryFormContent.email}
            onChange={(e) =>
              setEntryFormContent((prev) => ({
                ...prev,
                email: e.target.value,
              }))
            }
            placeholder="Napisz e-mail"
            className="w-full"
          />
        </div>
        <div className="field">
          <label>Firma</label>
          <Dropdown
            value={entryFormContent.companyId}
            options={dropdownConfigs.transporters || []}
            onChange={(e) =>
              setEntryFormContent((prev) => ({
                ...prev,
                companyId: e.value as number | null,
              }))
            }
            optionLabel="label"
            optionValue="value"
            placeholder="Wybierz firmę"
            className="w-full"
            showClear
          />
        </div>
        <div className="field">
          <label>Kierownik</label>
          <Dropdown
            value={entryFormContent.managerId}
            options={dropdownConfigs.organizationalUsers || []}
            onChange={(e) =>
              setEntryFormContent((prev) => ({
                ...prev,
                managerId: e.value as number | null,
              }))
            }
            optionLabel="label"
            optionValue="value"
            placeholder="Wybierz kierownika"
            className="w-full"
            showClear
          />
        </div>
        <div className="field">
          <label>Ciężarówki</label>
          <MultiSelect
            display="chip"
            value={entryFormContent.trucksIds ?? []}
            options={dropdownConfigs.trucks || []}
            onChange={(e) => handleCellContentChange("trucksIds", e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Wybierz ciężarówki"
            className="w-full"
            filter
            resetFilterOnHide={true}
            onFilter={(e) => {
              dropdownFilterRef.current = e.filter;
            }}
            panelFooterTemplate={
              <DropdownOpenAddNew<Driver>
                onAddNew={trucksColumn?.allowAddNew}
                row={entryFormContent}
                filterValue={dropdownFilterRef}
              />
            }
            emptyFilterMessage="Nie znaleziono wyników"
          />
        </div>
        <div className="field">
          <label>Przyczepy</label>
          <MultiSelect
            display="chip"
            value={entryFormContent.trailersIds ?? []}
            options={dropdownConfigs.trailers || []}
            onChange={(e) => handleCellContentChange("trailersIds", e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Wybierz ciężarówki"
            className="w-full"
            filter
            resetFilterOnHide={true}
            onFilter={(e) => {
              dropdownFilterRef.current = e.filter;
            }}
            panelFooterTemplate={
              <DropdownOpenAddNew<Driver>
                onAddNew={trailersColumn?.allowAddNew}
                row={entryFormContent}
                filterValue={dropdownFilterRef}
              />
            }
            emptyFilterMessage="Nie znaleziono wyników"
          />
        </div>
      </Dialog>
    </>
  );
}
